import React, { useEffect, useState } from 'react';
import { Form, FormInstance } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'hooks/store';
import {
  getResourceListAction,
  isLoadingResourcesSelector,
  resourcesEntitiesSelector,
  resourcesIdsSelector,
} from 'state/slices/resource';
import {
  Checkbox,
  Input,
  Modal,
  Select,
} from 'UI';
import { UnitEnum, UnitMapping } from 'constants/unit';

import {
  FormContainer,
  BaseFormItem,
  InlineButton,
  ResourcesTitle,
  ResourceFormItem,
  AddResourceButton,
  RemoveResourceButton,
  RemoveResourceIcon,
  ResourceFormRow,
} from './style';

interface TrayResourceFormField {
  resourceId: string,
  resourceConsumption: string
}

export interface TrayFormData {
  capacity: string
  isHydroponic: boolean
  resources: TrayResourceFormField[]
}

interface TrayModalProps {
  formHandler: FormInstance<TrayFormData>
  onSubmit(data: unknown): void
  onCancel(): void
  curTray?: TrayFormData & {
    id?: string
  }
  isLoading: boolean
  isVisible: boolean
}

const TrayModal = ({
  formHandler,
  onSubmit,
  onCancel,
  curTray,
  isLoading,
  isVisible,
}: TrayModalProps): JSX.Element => {
  const dispatch = useDispatch();
  const [selectedResources, setSelectedResources] = useState(curTray?.resources || []);
  const createdResourcesEntities = useSelector(resourcesEntitiesSelector);
  const createdResourcesIds = useSelector(resourcesIdsSelector);
  const isLoadingCreatedResources = useSelector(isLoadingResourcesSelector);

  const finalLoading = isLoading || isLoadingCreatedResources;

  useEffect(() => {
    setSelectedResources(curTray?.resources || []);
  }, [curTray?.id]);

  useEffect(() => {
    dispatch(getResourceListAction({
      isPaginated: false,
    }));
  }, [isVisible]);

  const onResourceSelectChange = (resourceId: string, index: number) => {
    const currentFormResources: TrayResourceFormField[] = formHandler.getFieldValue('resources');
    if (!currentFormResources) {
      return;
    }
    const updatedSelectedResources: typeof currentFormResources = [
      ...currentFormResources.slice(0, index),
      {
        resourceId,
        resourceConsumption: currentFormResources[index]?.resourceConsumption || '',
      },
      ...currentFormResources.slice(index + 1),
    ];
    setSelectedResources(updatedSelectedResources);
  };

  const onResourceRowRemove = (formRemove: (index: number | number[]) => void, index: number) => {
    formRemove(index);
    const updatedSelectedResources = selectedResources.filter((r, rIndex) => index !== rIndex);
    setSelectedResources(updatedSelectedResources);
  };

  return (
    <Modal
      visible={isVisible}
      title={curTray ? 'Editar bandeja' : 'Nova bandeja'}
      destroyOnClose
      onCancel={onCancel}
      afterClose={onCancel}
      zIndex={90}
      footer={[
        <InlineButton
          key="cancel"
          onClick={onCancel}
          type="ghost"
        >
          Cancelar
        </InlineButton>,
        <InlineButton
          key="submit"
          onClick={onSubmit}
          disabled={finalLoading}
          loading={finalLoading}
        >
          Salvar bandeja
        </InlineButton>,
      ]}
    >
      <FormContainer
        layout="vertical"
        form={formHandler}
        requiredMark={false}
        onFinish={onSubmit as (val: unknown) => void}
      >
        <BaseFormItem
          label="Tamanho"
          name="capacity"
          style={{
            width: 'fit-content',
          }}
          rules={[
            {
              required: true,
              message: 'Insira um tamanho',
            },
          ]}
          initialValue={curTray?.capacity}
        >
          <Input type="number" disabled={finalLoading} suffix="Células" />
        </BaseFormItem>
        <BaseFormItem
          label="Hidropônica"
          name="isHydroponic"
          valuePropName="checked"
          initialValue={curTray?.isHydroponic || false}
        >
          <Checkbox />
        </BaseFormItem>
        <ResourcesTitle>Insumos</ResourcesTitle>
        <Form.List
          name="resources"
          initialValue={selectedResources}
        >
          {(fields, { add, remove }) => (
            <>
              {fields.map((resourceField, index) => (
                <ResourceFormRow
                  key={resourceField.key}
                >
                  <ResourceFormItem
                    label="Recurso"
                    name={[resourceField.name, 'resourceId']}
                    validateTrigger="onSubmit"
                    rules={[
                      {
                        required: true,
                        message: 'Selecione um recurso',
                      },
                      {
                        message: 'Insumo repetido.',
                        validator: () => {
                          const resourceId: string | undefined = formHandler.getFieldValue(['resources', resourceField.name, 'resourceId']);
                          if (!resourceId) {
                            // Handled in another rule
                            return Promise.resolve();
                          }

                          const filteredResources = selectedResources
                            .filter((r) => r.resourceId === resourceId);
                          if (filteredResources.length > 1) {
                            return Promise.reject();
                          }

                          return Promise.resolve();
                        },
                      },
                    ]}
                  >
                    <Select
                      disabled={isLoading}
                      className="resource-name-input"
                      defaultActiveFirstOption
                      onChange={(val) => {
                        onResourceSelectChange(val, index);
                      }}
                    >
                      {createdResourcesIds.map((resourceId) => (
                        <Select.Option
                          value={resourceId}
                          key={resourceId}
                        >
                          {createdResourcesEntities[resourceId]?.name}
                        </Select.Option>
                      ))}
                    </Select>
                  </ResourceFormItem>
                  <ResourceFormItem
                    label="Consumo"
                    name={[resourceField.name, 'resourceConsumption']}
                    rules={[
                      {
                        required: true,
                        message: 'Insira uma capacidade',
                      },
                    ]}
                  >
                    <Input
                      type="number"
                      min={0}
                      disabled={finalLoading}
                      suffix={(() => {
                        const unit: UnitEnum = selectedResources[index]
                          ? (
                            createdResourcesEntities[selectedResources[index]?.resourceId || '']?.unit || UnitEnum.UNIT
                          ) : UnitEnum.UNIT;
                        return UnitMapping[unit].name;
                      })()}
                    />
                  </ResourceFormItem>
                  <RemoveResourceButton onClick={() => onResourceRowRemove(remove, index)}>
                    <RemoveResourceIcon />
                  </RemoveResourceButton>
                </ResourceFormRow>
              ))}
              <AddResourceButton
                type="dashed"
                onClick={() => add({
                  resourceId: undefined,
                  resourceConsumption: undefined,
                }, fields.length)}
                icon={<PlusOutlined size={14} />}
              >
                Adicionar insumo
              </AddResourceButton>
            </>
          )}
        </Form.List>
      </FormContainer>
    </Modal>
  );
};

TrayModal.defaultProps = {
  curTray: undefined,
};

export default TrayModal;
